import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import Layout from '../components/layout/Layout';
import { useLanguage } from '../i18n';

const HomePage = () => {
  const { t, getProfile, getHighlights } = useLanguage();
  const profile = getProfile();
  const highlights = getHighlights();

  return (
    <Layout>
      <section className="min-h-[calc(100vh-5rem)] flex items-center">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12 lg:py-20 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            <div className="lg:col-span-7 order-2 lg:order-1">
              <div className="mb-6">
                <span className="label-mono">{t('home.label')}</span>
              </div>

              <h1 className="hero-title mb-6">
                {t('home.title1')}<br />{t('home.title2')}<br />{t('home.title3')}
              </h1>

              <p className="font-body text-base text-slate-600 leading-relaxed mb-8 max-w-xl text-justify">
                {profile.summary}
              </p>

              <div className="flex flex-wrap gap-3 mb-8">
                {highlights.map((highlight, index) => (
                  <Badge 
                    key={index}
                    variant="outline"
                    className="font-mono text-xs uppercase tracking-wider rounded-none border-slate-200 text-slate-600 px-3 py-1.5"
                  >
                    {highlight}
                  </Badge>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <div className="flex items-center gap-2 text-slate-600">
                  <MapPin className="w-4 h-4" />
                  <span className="font-body text-sm">{profile.location}</span>
                </div>
              </div>
            </div>

            <div className="lg:col-span-5 order-1 lg:order-2">
              <div className="bg-white border border-slate-200 p-8 lg:p-10">
                <div className="grid grid-cols-2 gap-6">
                  <div className="border-r border-slate-200 pr-6">
                    <span className="font-display text-4xl lg:text-5xl font-semibold text-blue-600">9+</span>
                    <p className="font-mono text-xs uppercase tracking-wider text-slate-500 mt-2">{t('home.yearsExp')}</p>
                  </div>
                  <div className="pl-2">
                    <span className="font-display text-4xl lg:text-5xl font-semibold text-teal-600">15+</span>
                    <p className="font-mono text-xs uppercase tracking-wider text-slate-500 mt-2">{t('home.projectsDelivered')}</p>
                  </div>
                </div>
                <div className="mt-8 pt-6 border-t border-slate-200 grid grid-cols-2 gap-6">
                  <div className="border-r border-slate-200 pr-6">
                    <span className="font-display text-4xl lg:text-5xl font-semibold text-slate-800">5</span>
                    <p className="font-mono text-xs uppercase tracking-wider text-slate-500 mt-2">{t('home.sectorsCovered')}</p>
                  </div>
                  <div className="pl-2">
                    <span className="font-display text-4xl lg:text-5xl font-semibold text-slate-800">2</span>
                    <p className="font-mono text-xs uppercase tracking-wider text-slate-500 mt-2">{t('home.countries')}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-16 pt-12 border-t border-slate-200">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Link to="/skills" className="group p-6 bg-white border border-slate-200 hover:border-blue-600 transition-colors">
                <h3 className="font-display text-lg font-medium text-slate-800 mb-2">{t('home.skillsCard.title')}</h3>
                <p className="font-body text-sm text-slate-500 mb-4">{t('home.skillsCard.description')}</p>
                <span className="font-mono text-xs text-blue-600 flex items-center gap-1 group-hover:gap-2 transition-all">
                  {t('home.skillsCard.cta')} <ArrowRight className="w-3 h-3" />
                </span>
              </Link>
              <Link to="/missions" className="group p-6 bg-white border border-slate-200 hover:border-blue-600 transition-colors">
                <h3 className="font-display text-lg font-medium text-slate-800 mb-2">{t('home.missionsCard.title')}</h3>
                <p className="font-body text-sm text-slate-500 mb-4">{t('home.missionsCard.description')}</p>
                <span className="font-mono text-xs text-blue-600 flex items-center gap-1 group-hover:gap-2 transition-all">
                  {t('home.missionsCard.cta')} <ArrowRight className="w-3 h-3" />
                </span>
              </Link>
              <Link to="/employers" className="group p-6 bg-white border border-slate-200 hover:border-blue-600 transition-colors">
                <h3 className="font-display text-lg font-medium text-slate-800 mb-2">{t('home.employersCard.title')}</h3>
                <p className="font-body text-sm text-slate-500 mb-4">{t('home.employersCard.description')}</p>
                <span className="font-mono text-xs text-blue-600 flex items-center gap-1 group-hover:gap-2 transition-all">
                  {t('home.employersCard.cta')} <ArrowRight className="w-3 h-3" />
                </span>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default HomePage;
